if (!customElements.get('reserve-widget')) {
    customElements.define(
      'reserve-widget',
      class ReserveWidget extends HTMLElement {
        constructor() {
          super();

          this.elements = {
            formattedExpiredDate: this.querySelector('#formatted-expired-date'),
            formattedEndDate: this.querySelector('#formatted-end-date'),
            formattedUpcomingDate: this.querySelector('#formatted-upcoming-date'),
            daysRemaining: this.querySelector('#days-remaining'),
            dayLabel: this.querySelector('#day-label'),
            percentValue: this.querySelector('#percent-value'),
            progressFill: this.querySelector('#progress-fill'),
          };

          this.today = new Date();
          this.today.setHours(0, 0, 0, 0);

          this.msPerDay = 86400000;
        }

        /*
         * Prepare dates and initialize the widget when it is connected to the DOM
         */
        connectedCallback() {
            this.init();
        }

        /*
         * Ordinal suffix function (1st, 2nd, 3rd, etc.)
         * @param {number} n - The number to get the ordinal suffix for
         * @returns {string} The ordinal suffix for the given number
         */
        getOrdinalSuffix(n) {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;

            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        }

        /*
         * Format date as "MONTH DAYth"
         * @param {Date} date - The date to format
         * @returns {string} The formatted date
         */
        formatDate(date) {
            const month = date.toLocaleString('en-US', { month: 'long' }); // e.g., AUGUST
            const day = date.getDate();
            return `${month} ${this.getOrdinalSuffix(day)}`; // e.g., AUGUST 8TH
        }

        /*
         * Parse date to local midnight
         * @param {string} str - The date to parse
         * @returns {Date} The parsed date
         */
        parseDateToLocalMidnight(str) {
            // assumes "YYYY-MM-DD" or ISO string
            const d = new Date(str);
            d.setHours(0,0,0,0);

            return d;
        }

        /*
         * Get the first Monday of a given month
         * @param {number} year - The year
         * @param {number} month - The month (0-indexed)
         * @returns {Date} The first Monday of the month
         */
        getFirstMondayOfMonth(year, month) {
            const first = new Date(year, month, 1);
            // Monday is day 1; calculate days until Monday
            const dayOfWeek = first.getDay();
            const daysUntilMonday = (8 - dayOfWeek) % 7 || 7;
            // If the 1st is already Monday, daysUntilMonday would be 7 from the formula, so handle that
            if (dayOfWeek === 1) {
                return first;
            }
            return new Date(year, month, 1 + daysUntilMonday);
        }

        /*
         * Calculate days remaining and elapsed
         * @returns {object} The days remaining and elapsed and current percent
         */
        calculateDaysRemainingAndElapsed() {
            const daysRemaining = Math.max(0, Math.ceil((this.reserveDate - this.today) / this.msPerDay));
            const startOfMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
            const daysElapsed = Math.max(0, Math.floor((this.today - startOfMonth) / this.msPerDay));
            const currentPercent = Math.min(100, this.startPercent + (this.increment * daysElapsed));

            return { daysRemaining, daysElapsed, currentPercent };
        }

        /*
         * Prepare dates — all dates are now auto-calculated
         */
        prepareDates() {
            const year = this.today.getFullYear();
            const month = this.today.getMonth();

            // Expired (sold out): first Monday of previous month
            const prevMonth = month === 0 ? 11 : month - 1;
            const prevYear = month === 0 ? year - 1 : year;
            this.expiredDate = this.getFirstMondayOfMonth(prevYear, prevMonth);

            // Reserve date: use restock-date metafield if provided, otherwise tomorrow
            const restockAttr = this.getAttribute('restock-date');
            if (restockAttr && restockAttr.trim() !== '') {
                this.reserveDate = this.parseDateToLocalMidnight(restockAttr);
            } else {
                this.reserveDate = new Date(year, month, this.today.getDate() + 1);
            }

            // Upcoming batch: first Monday of next month
            const nextMonth = month === 11 ? 0 : month + 1;
            const nextYear = month === 11 ? year + 1 : year;
            this.upcomingDate = this.getFirstMondayOfMonth(nextYear, nextMonth);
        }

        /*
         * Update the progress
         */
        updateProgress() {
            const { daysRemaining, daysElapsed, currentPercent } = this.calculateDaysRemainingAndElapsed();

            this.elements.daysRemaining && (this.elements.daysRemaining.textContent = daysRemaining);
            this.elements.dayLabel && (this.elements.dayLabel.textContent = daysRemaining === 1 ? 'day' : 'days');

            this.elements.percentValue && (this.elements.percentValue.textContent = currentPercent + '%');
            this.elements.progressFill && (this.elements.progressFill.style.width = currentPercent + '%');
        }

        /*
         * Update all date displays
         */
        updateDateDisplays() {
            if (this.elements.formattedExpiredDate) {
                this.elements.formattedExpiredDate.textContent = this.formatDate(this.expiredDate);
            }

            if (this.elements.formattedEndDate) {
                this.elements.formattedEndDate.textContent = this.formatDate(this.reserveDate);
            }

            if (this.elements.formattedUpcomingDate) {
                this.elements.formattedUpcomingDate.textContent = this.formatDate(this.upcomingDate);
            }
        }

        /*
         * Initialize the widget
         */
        init() {
            this.prepareDates();
            this.updateProgress();
            this.updateDateDisplays();
        }

        get increment() {
            return parseInt(this.getAttribute('increment'));
        }
        get startPercent() {
            return parseInt(this.getAttribute('start-percent')) || 10;
        }
      }
    );
  }
